import { fmt, fmtUsd, fmtPct } from '@/lib/utils'

/// Builds the shareable text for this wallet's Horizon Protocol position - proof of profitability
/// (what's actually been earned, and at what real APY), not just a current-balance snapshot.
/// Every earned/APY line is conditional on the wallet actually holding that position - never
/// boasts a rate or amount it isn't really earning. Shared by ShareCard's "Copy Stats" button and
/// the "Share on X" button (now on Portfolio's Your Position panel, not ShareCard itself) so both
/// post the exact same text instead of two independently-maintained copies drifting apart.
export function buildShareText({
  horizonBalance, horizonValueUsd, hasHorizonPrice,
  totalEarnedHorizon, totalEarnedUsd,
  earnValueUsd, earnAprPct, stakedHorizon, borrowNetUsd, revenueShareUsd,
}) {
  const lines = [
    '🌙 My Horizon Protocol stats:',
    `💰 ${fmt(horizonBalance, 2)} HORIZON${hasHorizonPrice ? ` (${fmtUsd(horizonValueUsd)})` : ''}`,
  ]

  // Proof of profitability: one combined "earned so far" figure across every reward stream this
  // wallet has pending (farm, both money-market reserves' supply + borrow incentives, staking) -
  // a USD total when a real HORIZON price is available (the strongest, single-number flex), a plain
  // HORIZON amount otherwise. Omitted entirely when nothing's been earned yet rather than posting
  // "$0.00 earned so far".
  if (totalEarnedHorizon > 0) {
    const earnedLine = hasHorizonPrice ? fmtUsd(totalEarnedUsd) : `${fmt(totalEarnedHorizon, 4)} HORIZON`
    lines.push(`🎯 ${earnedLine} earned so far`)
  }

  // Real advertised rates - only named when this wallet actually holds a position earning them,
  // never a generic "APY up to X%" that doesn't reflect what's really happening in this wallet.
  if (earnValueUsd > 0) {
    lines.push(`📈 Earning ${fmtPct(earnAprPct)} APY on ${fmtUsd(earnValueUsd)} in Earn`)
  }
  if (stakedHorizon > 0) {
    lines.push(`🔒 ${fmt(stakedHorizon, 2)} HORIZON locked for revenue share`)
  }
  // Real protocol revenue share, earned by the locked HORIZON above - only shown when this
  // wallet actually has some pending, same conditional-line rule every other line here follows.
  if (revenueShareUsd > 0) {
    lines.push(`💸 ${fmtUsd(revenueShareUsd)} pending in Revenue Share`)
  }
  // Omitted entirely at exactly $0 net - only shown when this wallet actually holds a position,
  // same conditional-line rule every other line here follows.
  if (borrowNetUsd !== 0) {
    lines.push(`🏦 ${fmtUsd(borrowNetUsd)} net in the Money Market`)
  }
  return lines.join('\n')
}

/// Opens a pre-filled X/Twitter share intent for this wallet's stats. Fire-and-forget, same
/// `window.open` intent-link pattern used nowhere else in this app (this is the only outbound
/// share action).
export function shareOnX(props) {
  const shareText = buildShareText(props)
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://app.horizon.win'
  const tweet = `${shareText}\n\n${siteUrl}`
  const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweet)}`
  window.open(url, '_blank', 'noopener,noreferrer')
}
